import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { Priority } from '../../enums';
import { BaseEntity } from './base.entity';
import { CompanyEntity } from './company.entity';
import type { IssueActivityEntity } from './issue-activity.entity';
import { IssueFileEntity } from './issue-file.entity';
import { IssueLinkEntity } from './issue-link.entity';
import { IssueNoteEntity } from './issue-note.entity';
import { IssueStatusHistoryEntity } from './issue-status-history.entity';
import { IssueUserEntity } from './issue-user.entity';
import { PublicUserEntity } from './public-user.entity';
import { UserEntity } from './user.entity';

@Index('IDX_issue_status', ['status'], {})
@Index('IDX_issue_statusUpdatedDatetime', ['statusUpdatedDatetime'], {})
@Index('IDX_issue_type', ['type'], {})
@Index('IDX_issue_priority', ['priority'], {})
@Index('IDX_issue_assignee', ['assigneeUserId'], {})
@Index('IDX_issue_submitter', ['submitterPublicUserId'], {})
@Index('IDX_issue_company', ['companyId'], {})
@Entity('issue')
export class IssueEntity extends BaseEntity {
	// Plain text. With rich text (PD-16475) this is the copy derived from
	// `descriptionHtml`, kept so everything that reads the description as
	// text (the legacy CRM's client inquiries, emails, search, AI tools, the
	// list column) is unchanged.
	@Column('longtext', { name: 'description', nullable: true })
	public description!: string | null;

	// PD-16475 — the description's sanitized rich text (see
	// `sanitizeIssueRichText`). NULL for issues written before rich text or
	// through a plain-text path; those display `description`.
	@Column('longtext', { name: 'descriptionHtml', nullable: true })
	public descriptionHtml!: string | null;

	@Column('varchar', { name: 'name', length: 255 })
	public name!: string;

	@Column('varchar', { name: 'status', length: 64, default: () => "'To-Do'" })
	public status!: string;

	@Column('datetime', { name: 'statusUpdatedDatetime', nullable: true })
	public statusUpdatedDatetime!: Date | null;

	@Column('varchar', { name: 'type', nullable: true, length: 64 })
	public type!: string | null;

	@Column('enum', {
		name: 'priority',
		enum: Priority,
		default: Priority.NORMAL,
	})
	public priority!: Priority;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@Column('int', {
		name: '_fk_user__id_assignee',
		nullable: true,
		unsigned: true,
	})
	public assigneeUserId!: number | null;

	@ManyToOne(() => UserEntity, (user) => user.assignedIssues, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id_assignee', referencedColumnName: 'id' }])
	public assignee!: UserEntity | null;

	// PD-13705 — set only when the issue was submitted by a customer through
	// the e-commerce portal; identifies the portal (public) user who raised
	// it so the CRM can show the submitter's name/email. NULL for issues
	// created inside the CRM.
	@Column('int', {
		name: 'fk_public_user__id_submitter',
		nullable: true,
		unsigned: true,
	})
	public submitterPublicUserId!: number | null;

	@ManyToOne(() => PublicUserEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_public_user__id_submitter', referencedColumnName: 'id' },
	])
	public submitterPublicUser!: PublicUserEntity | null;

	// PD-16475 — the company the issue belongs to, stamped when the issue is
	// created (portal brand, then the client's primary company, then the
	// creator's only company). Drives the Issues list Company column and filter.
	// Visibility is NOT decided by this column: the PD-13121 company scope
	// still resolves through the linked client / assignee / creator, so an
	// issue never becomes visible to someone just because this was set.
	@Column('int', {
		name: 'fk_company__id',
		nullable: true,
		unsigned: true,
	})
	public companyId!: number | null;

	@ManyToOne(() => CompanyEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id', referencedColumnName: 'id' }])
	public company!: CompanyEntity | null;

	@OneToMany(() => IssueNoteEntity, (issueNote) => issueNote.fkIssue, {})
	public issueNotes!: Array<IssueNoteEntity>;

	@OneToMany(() => IssueUserEntity, (issueUser) => issueUser.fkIssue, {})
	public issueUsers!: Array<IssueUserEntity>;

	@OneToMany(() => IssueStatusHistoryEntity, (history) => history.fkIssue, {})
	public statusHistory!: Array<IssueStatusHistoryEntity>;

	@OneToMany(() => IssueLinkEntity, (link) => link.fkIssue, {})
	public issueLinks!: Array<IssueLinkEntity>;

	@OneToMany(
		'IssueActivityEntity',
		(activity: IssueActivityEntity) => activity.fkIssue,
		{},
	)
	public activities!: Array<IssueActivityEntity>;

	@OneToMany(() => IssueFileEntity, (issueFile) => issueFile.fkIssue, {})
	public issueFiles!: Array<IssueFileEntity>;
}
